<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            A 360 photo is a spherical image that captures a full 360°
            horizontal view and 180° vertical view simultaneously — creating a
            complete photographic record of every direction from a single point
            in space. Unlike a standard photograph that captures a rectangular
            slice of your field of view, a 360 photo captures the entire
            environment around the photographer. When you view a 360 photo, you
            can pan, zoom, and look in any direction, including straight up and
            straight down.
          </p>
        </section>

        <section class="key-takeaways">
          <h2>Key Takeaways</h2>
          <ul>
            <li>A 360 photo captures a complete spherical view of a scene — 360° horizontally and 180° vertically.</li>
            <li>360 photos are created either with a 360° camera in a single shot or by stitching multiple phone images.</li>
            <li>They can be viewed interactively on phones, desktop browsers, and VR headsets.</li>
            <li>Common uses: virtual tours, Google Maps, social media, and VR experiences.</li>
            <li>Modern smartphones with AI apps produce 360° photos comparable to dedicated cameras.</li>
          </ul>
        </section>

        <section>
          <h2>How 360 Photos Are Created</h2>

          <h3>The Technical Process</h3>
          <p>
            360 photos are created through a process called
            <strong>image stitching</strong>. Multiple overlapping photographs —
            taken in different directions from the same point — are
            mathematically aligned and blended to create a seamless sphere. The
            resulting file is typically stored as an equirectangular image: a
            flat, rectangular file (usually with a 2:1 aspect ratio) that
            represents the spherical surface mapped to a flat plane, similar to
            how world maps represent the spherical Earth on a flat page.
          </p>
          <p>
            Dedicated 360 cameras (like the Insta360, Ricoh Theta, or similar
            devices) do this automatically using dual fisheye lenses that each
            capture approximately 220° of view, with the overlap between them
            stitched together by onboard processing. Smartphone apps like
            Travvir achieve the same result by guiding you to capture multiple
            frames in sequence and performing the stitching in software. Try the
            <a :href="`${mainSiteUrl}/360camera`">Travvir 360 Camera app</a>
            to create spherical photos and open them in a built-in 360 viewer.
          </p>

          <h3>Equirectangular Format Explained</h3>
          <p>
            If you download a 360 photo file and open it in a standard image
            viewer, it looks warped and stretched — especially at the top and
            bottom. This distortion is the natural result of projecting a sphere
            onto a flat surface. The left and right edges of the image are
            actually the same point — looking directly behind you. The extreme
            top of the image is the zenith (directly overhead), and the extreme
            bottom is the nadir (directly underfoot).
          </p>
          <p>
            Specialised 360 viewers (like those used in Travvir, Google Street
            View, Facebook, YouTube, and Matterport) take this flat
            equirectangular file and re-project it onto a sphere, allowing the
            viewer to look around naturally. The metadata embedded in the file
            (specifically the XMP metadata tags used by the JPEG standard) tells
            these viewers that this is a 360 image and which projection type it
            uses.
          </p>
        </section>

        <section>
          <h2>Types of 360 Content</h2>

          <h3>Static 360 Photos</h3>
          <p>
            A single spherical image from one fixed point in space. When you
            look at it in a viewer, you can rotate your view in any direction,
            but you cannot move through the space. These are the most common
            type of 360 content and the building blocks of virtual tours.
          </p>

          <h3>360 Virtual Tours</h3>
          <p>
            A collection of 360 photos taken from multiple positions within a
            space, connected by navigation hotspots that allow viewers to move
            from one position to another. As you navigate, the perspective
            shifts realistically, creating the sensation of walking through the
            environment. This is what Travvir and platforms like Matterport
            create.
          </p>

          <h3>360 Video</h3>
          <p>
            Similar to 360 photos in terms of the spherical capture method, but
            records continuous video rather than still images. 360 video cannot
            be navigated (you cannot choose where to go), but the view is
            interactive — you can look in any direction while the video plays.
            360 video files are extremely large and typically streamed rather
            than downloaded.
          </p>

          <h3>Photogrammetry and 3D Scans (vs 360)</h3>
          <p>
            Photogrammetry-based tools like Matterport produce a different type
            of output: a 3D mesh model of a space derived from many overlapping
            photographs processed by AI. This creates a navigable 3D model
            rather than a collection of photographic spheres. The result looks
            different — more like a video game environment than photographic
            imagery — and requires significantly more processing power and
            specialised hardware.
          </p>
        </section>

        <section>
          <h2>How 360 Photos Are Displayed</h2>

          <h3>Browser-Based Viewers</h3>
          <p>
            Platforms like Travvir, Kuula, and Momento360 display 360 photos via
            JavaScript WebGL rendering in a standard web browser. The
            equirectangular image is loaded and re-projected onto a sphere in
            real time. This allows 360 photos to be embedded in websites with an
            iframe tag, shared via links, or published directly to social media
            platforms.
          </p>

          <h3>Mobile App Display</h3>
          <p>
            360 photos viewed in mobile apps can use the device's gyroscope to
            enable orientation-tracked viewing: tilting and rotating your
            physical phone changes what you're looking at in the 360 scene. This
            creates an immersive viewing experience without a headset — you
            physically move your device to look around the space.
          </p>

          <h3>VR Headset Display</h3>
          <p>
            The same equirectangular 360 photo file that displays in a browser
            can be viewed in a VR headset (Meta Quest, Apple Vision Pro,
            Cardboard, etc.) for a fully immersive experience. Head tracking
            replaces mouse/touch navigation, and stereoscopic 360 content
            creates the illusion of depth within the scene.
          </p>
        </section>

        <section>
          <h2>Common Questions About 360 Photos</h2>

          <h3>Can I Take a 360 Photo with My Normal Smartphone?</h3>
          <p>
            Yes. Smartphone apps including Travvir, Google Street View, and
            others use your phone's single camera and guided capture to create
            360 photos. The app prompts you to rotate and capture multiple
            overlapping frames, then stitches them automatically. The result is
            a genuine 360 photo that can be shared anywhere standard 360 content
            is supported. See our guide on
            <a href="/blog/en/do-you-need-360-camera"
              >whether you need a dedicated 360 camera</a
            >
            for a detailed comparison.
          </p>

          <h3>What's the File Size of a 360 Photo?</h3>
          <p>
            A typical 360 photo created by a smartphone app ranges from
            <strong>8–25 MB</strong>
            as a JPEG file. Dedicated 360 cameras produce files in the range of
            <strong>20–80 MB</strong> at higher resolutions. After platform
            compression (Google, Facebook, and most web viewers apply
            compression), the effective delivered file size is typically 2–8 MB
            for normal display conditions.
          </p>

          <h3>How Are 360 Photos Different from Panoramic Photos?</h3>
          <p>
            A panoramic photo is a wide-angle image — typically wider than a
            standard photo but still a flat rectangle. You can print a panorama
            and hang it on a wall. A 360 photo is a complete sphere — it wraps
            around you in every direction. A panoramic photo is a subset of what
            you could see from a given point; a 360 photo is the complete view
            of everything visible from that point.
          </p>
        </section>

        <section>
          <h2>Conclusion: The Essentials of 360 Photography</h2>
          <p>
            A 360 photo captures the complete environment from a single point in
            space, stored as an equirectangular image that viewers can explore
            interactively. The technology is now accessible through any modern
            smartphone via apps like Travvir, and the output can be published to
            Google, websites, social media, and VR platforms using the same
            file.
          </p>
          <p>
            For businesses, 360 photos form the foundation of virtual tours that
            improve local SEO, increase conversion rates, and help customers
            make confident purchasing decisions. For individuals, they're a
            powerful way to share places and experiences in a format that goes
            far beyond what a standard photograph can convey.
          </p>
        </section>
        <section>
          <h2>Continue Reading</h2>
          <ul>
          <li><a :href="`${mainSiteUrl}/360camera`">Travvir 360 Camera App</a></li>
          <li><a href="/blog/en/how-to-take-360-photos-smartphone">How to Take 360 Photos with Your Phone</a></li>
          <li><a href="/blog/en/shoot-360-photos-with-phone">Shoot Professional 360 Photos</a></li>
          <li><a href="/blog/en/fix-blurry-360-photos">Fix Blurry 360 Photos</a></li>
          <li><a href="/blog/en/360-virtual-tour-image-size">Best Image Size for 360 Tours</a></li>
          <li><a href="/blog/en/smartphone-panorama-vs-360-virtual-tour">Panorama vs 360 Virtual Tour</a></li>
          <li><a href="/blog/en/share-360-photo-social-media">Share 360 Photos on Social Media</a></li>
          <li><a href="/blog/en/free-virtual-tour-creator">Complete Guide to 360 Virtual Tours</a></li>
          <li><a href="/blog/en/do-you-need-360-camera">Do You Need a 360 Camera?</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;
const mainSiteUrl = config.public.baseUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "what-is-a-360-photo",
);

useBlogSeo({
  title: "What Is a 360 Photo? Types, How It Works & Uses [2026] | Travvir",
  description:
    "Learn what a 360 photo is, how it works, and the different types. Discover how businesses use 360° photos for virtual tours, social media, and Google Maps.",
  keywords:
    "what is a 360 photo, how 360 photos work, 360 photography explained, 360 image types, equirectangular photo",
  slug: "what-is-a-360-photo",
  image: postData?.image,
  postData,
  lang: "en",
});

</script>
